import { action, computed, makeObservable, observable } from 'mobx';
import { RealityCheckState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/realityCheck/RealityCheck.state';
import { SignupRollingNetDepositLimitState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimit.state';
import { SignUpParamsType } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/SignUpParams';
import { CreateAccountStep } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/CreateAccount.state';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { LimitsConfirmationState } from './LimitsConfirmation.state';
import { AccountAgeVerificationEnum, AccountStatusEnum } from 'src_server/trpc/types/createAccountPrimary';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';

interface CallbacksType {
    readonly redirectToVerificationFailed: (accountId: number | null) => void;
    readonly redirectToGamStopAccountFailed: () => void;
    readonly redirectToNextStep: () => void;
    readonly setLoading: (isLoading: boolean) => void;
}

export class ResponsibleGamblingState {
    @observable public userId: number | null = null;

    public readonly realityCheckState: RealityCheckState;
    public readonly limitsConfirmationState: LimitsConfirmationState;
    public readonly rollingNetDepositLimitState: SignupRollingNetDepositLimitState;

    public constructor(
        private readonly params: SignUpParamsType,
        private readonly callbacks: CallbacksType,
        private readonly createAccountStep: CreateAccountStep
    ) {
        makeObservable(this);

        this.realityCheckState = new RealityCheckState(this.params.common);
        this.limitsConfirmationState = new LimitsConfirmationState();
        this.rollingNetDepositLimitState = new SignupRollingNetDepositLimitState(
            this.params.common,
            this.createAccountStep
        );
    }

    @computed public get isButtonDisabled(): boolean {
        if (this.limitsConfirmationState.isButtonDisabled) {
            return true;
        }
        if (this.realityCheckState.isSwitched) {
            if (!this.realityCheckState.selectedDuration.isSelected) {
                return true;
            }
        }
        const config = ConfigComponents.get(this.params.common).config;
        if (config.rollingNetDepositLimit === true && this.rollingNetDepositLimitState.isErrorInput) {
            return true;
        }
        return false;
    }

    private onSubmitForm = async (): Promise<void> => {
        if (this.realityCheckState.isSwitched) {
            await this.realityCheckState.onSubmitRealityCheckForm();
        }
        const config = ConfigComponents.get(this.params.common).config;
        if (config.rollingNetDepositLimit === true) {
            await this.rollingNetDepositLimitState.onSubmit();
        }
    };

    public onMoveToNextStep = async (
        responsibleGambling: string,
        isDepositLimit?: boolean,
        isRealityCheck?: boolean
    ): Promise<void> => {
        this.callbacks.setLoading(true);
        const googleTagManager = GoogleTagManagerState.get(this.params.common);
        googleTagManager.gtmSignUpStepThree(responsibleGambling, isDepositLimit, isRealityCheck);

        const verificationResult = await this.waitForAccountVerification();

        if (verificationResult) {
            this.params.customKeyboard.scrollAsideToTop();
            this.callbacks.redirectToNextStep();
        } else {
            console.error('Verification failed', verificationResult);
        }
        this.callbacks.setLoading(false);
    };

    public onSubmitAndNext = async (): Promise<void> => {
        try {
            await this.onSubmitForm();
            await this.onMoveToNextStep(
                'activated',
                this.rollingNetDepositLimitState.setAtLeastOneLimit(),
                this.realityCheckState.isSwitched
            );
        } catch (e) {
            console.error('deposit limit and reality check error:', e);
        }
    };

    @action private setUserId = (userId: number | null): void => {
        this.userId = userId;
    };

    @action private waitForAccountVerification = async (): Promise<boolean> => {
        const config = ConfigComponents.get(this.params.common).config;
        if (config.timeoutToTestSignupFlow) {
            await timeout(30000);
        }

        const email = this.createAccountStep.welcomeState.emailState.value;
        const password = this.createAccountStep.welcomeState.passwordState.value;
        const token = this.createAccountStep.userCreationToken;

        if (token === null) {
            console.error('waitForAccountVerification -> missing creation token');
            return false;
        }

        for (let i = 0; i < 150; i++) {
            // 7min try login user, waiting for account verify.
            const accountStatus = await this.params.common.trpcClient.client.signup.checkStatusOfAccount.mutate({
                body: { token },
            });

            if (accountStatus.responseStatus === 'success') {
                const googleTagManager = GoogleTagManagerState.get(this.params.common);
                const config = ConfigComponents.get(this.params.common).config;
                const accountState = AccountState.get(this.params.common);
                const isBlockedAccount = accountStatus.data.status === AccountStatusEnum.BLOCKED;
                const isClosedAccount = accountStatus.data.status === AccountStatusEnum.CLOSED;
                const isActiveAccount = accountStatus.data.status === AccountStatusEnum.ACTIVE;
                const isSuspendedAccount = accountStatus.data.status === AccountStatusEnum.SUSPENDED;
                const isAgeVerificationPassed =
                    accountStatus.data.ageVerification === AccountAgeVerificationEnum.PASSED;
                const isAvVerificationFailed = accountStatus.data.ageVerification === AccountAgeVerificationEnum.FAILED;
                const isAvVerificationUnknown =
                    accountStatus.data.ageVerification === AccountAgeVerificationEnum.UNKNOWN;

                const contactPreferencesForm =
                    this.createAccountStep.yourDetailsState.contactPreferences.formatFormModel.result.value;

                if (isSuspendedAccount || isBlockedAccount || isClosedAccount) {
                    const loginUser = await accountState.loginUser(email, password);

                    if (loginUser.type === 'CreateSessionResponseOk') {
                        this.setUserId(loginUser.accountId);
                        googleTagManager.registerFinishedTag(loginUser.accountId);

                        if (contactPreferencesForm.type === 'ok') {
                            await this.params.common.trpcClient.client.accounts.changeAccountContactPreferencesNew.mutate(
                                {
                                    customerId: loginUser.accountId,
                                    contactPreferences: contactPreferencesForm.data.contactPreferences,
                                }
                            );
                        }
                    }

                    if (loginUser.type === 'CreateSessionResponseErrors') {
                        this.setUserId(loginUser.accountId ?? null);
                        googleTagManager.registerFinishedTag(loginUser.accountId);

                        if (
                            contactPreferencesForm.type === 'ok' &&
                            loginUser.accountId !== undefined &&
                            loginUser.accountId !== null
                        ) {
                            await this.params.common.trpcClient.client.accounts.changeAccountContactPreferencesNew.mutate(
                                {
                                    customerId: loginUser.accountId,
                                    contactPreferences: contactPreferencesForm.data.contactPreferences,
                                }
                            );
                        }

                        if (loginUser.error === 'gamstop_unauthorized') {
                            this.callbacks.setLoading(false);
                            this.callbacks.redirectToGamStopAccountFailed();
                            console.error('Verification error description: ', loginUser.error_description);

                            return false;
                        }
                    }
                }

                if ((config.signUpOmitAgeVerification === true || isAgeVerificationPassed) && isActiveAccount) {
                    const loginUser = await accountState.loginUser(email, password);
                    const usersState = UsersState.get(this.params.common);

                    if (config.ringFencedFundsSetByDefault === true) {
                        // set by default RingFencedFlag for new user
                        await usersState.onChangeRingFencedFlag();
                    }
                    if (loginUser.type === 'CreateSessionResponseOk') {
                        googleTagManager.registerFinishedTag(loginUser.accountId);

                        if (contactPreferencesForm.type === 'ok') {
                            await this.params.common.trpcClient.client.accounts.changeAccountContactPreferencesNew.mutate(
                                {
                                    customerId: loginUser.accountId,
                                    contactPreferences: contactPreferencesForm.data.contactPreferences,
                                }
                            );
                        }
                    }
                    return true;
                }

                if (
                    isBlockedAccount ||
                    isSuspendedAccount ||
                    isAvVerificationFailed ||
                    isAvVerificationUnknown ||
                    isClosedAccount
                ) {
                    this.callbacks.setLoading(false);
                    this.callbacks.redirectToVerificationFailed(this.userId);
                    this.setUserId(null);
                    return false;
                }
            }

            await timeout(3000);
        }
        return false;
    };
}
